import { AlertTriangle, CheckCircle, XCircle, Copy, Settings } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import type { CreateMachineResponse } from '../../types/machine-setup-types';

interface ResultStepProps {
  result: CreateMachineResponse | null;
  error: string | null;
  onCreateAnother: () => void;
  onCreateSameSettings: () => void;
  onCreateDifferentParams: () => void;
}

export function ResultStep({
  result,
  error,
  onCreateAnother,
  onCreateSameSettings,
  onCreateDifferentParams,
}: ResultStepProps) {
  const isSuccess = result?.isSuccess && result.machineId != null;

  if (isSuccess) {
    return (
      <div className="text-center py-8">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 mb-4">
          <CheckCircle className="h-8 w-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">Machine Created</h2>
        <p className="text-gray-600 mb-1">
          <span className="font-medium">{result.machineName}</span> was successfully created in Lynx.
        </p>
        <p className="text-sm text-gray-400 font-mono mb-4">Machine ID: {result.machineId}</p>
        <p className="text-xs text-gray-400 mb-6">
          Preset attributes have been applied. Verify the configuration in Lynx or use the validate
          endpoint to confirm all attributes are correct.
        </p>
        {result.warning && (
          <div className="flex items-start gap-2 text-left text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-4 py-3 max-w-md mx-auto mb-6">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-500" />
            <span>{result.warning}</span>
          </div>
        )}

        <p className="text-sm font-medium text-gray-700 mb-3">What would you like to do next?</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button variant="outline" onClick={onCreateSameSettings} className="gap-2">
            <Copy className="h-4 w-4" />
            Create more with same settings
          </Button>
          <Button variant="outline" onClick={onCreateDifferentParams} className="gap-2">
            <Settings className="h-4 w-4" />
            Create with different parameters
          </Button>
          <Button variant="ghost" onClick={onCreateAnother}>
            Done
          </Button>
        </div>
      </div>
    );
  }

  const errorMessage = error ?? result?.message ?? 'An unexpected error occurred.';

  return (
    <div className="text-center py-8">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100 mb-4">
        <XCircle className="h-8 w-8 text-red-600" />
      </div>
      <h2 className="text-2xl font-semibold text-gray-900 mb-2">Creation Failed</h2>
      <p className="text-sm text-red-600 bg-red-50 rounded-md px-4 py-3 border border-red-200 max-w-md mx-auto mb-8">
        {errorMessage}
      </p>
      <Button variant="outline" onClick={onCreateAnother}>
        Try Again
      </Button>
    </div>
  );
}
